import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

const SIZE = {
  xxs: 'px-1.5 py-0.5 text-xs rounded',
  xs: 'px-2.5 py-1.5 text-xs',
  sm: 'px-3 py-2 text-sm leading-4',
  default: 'px-6 py-3 text-base',
  lg: 'px-7 py-3 text-base',
  xl: 'px-8 py-3 text-base',
};

const ICON_SIZE = {
  xxs: 'h-4 w-4',
  xs: 'h-4 w-4',
  sm: 'h-5 w-5',
  default: 'h-5 w-5',
  lg: 'h-5 w-5',
  xl: 'h-5 w-5',
};

const COLOR = {
  primary:
    'transition shadow border-transparent text-white bg-gradient-to-r from-primary-600 to-primary-800 hover:from-primary-800 shadow',
  secondary:
    'transition shadow border-transparent text-white bg-gradient-to-r from-secondary-500 via-secondary-600 to-secondary-600',
  link:
    'transition text-primary-500 bg-white hover:text-primary-500 hover:bg-gray-50 border-none shadow',
  success: 'transition shadow border-transparent text-white bg-green-500',
  danger:
    'transition shadow border-transparent text-white bg-danger-600 hover:bg-danger-700 shadow',
  transparent:
    'transition shadow-none text-primary-800 bg-transparent border-transparent font-normal',
  outlinePrimary:
    'transition shadow-sm border-primary-800 text-primary-800 focus:ring-primary-500 shadow-primary-lg',
  outlineLight:
    'transition shadow-sm border-white text-white focus:ring-primary-500 shadow-lg',
  default: 'transition border-none text-primary-800 bg-white',
};

const Button = React.forwardRef(
  (
    {
      size,
      primary,
      secondary,
      success,
      danger,
      link,
      outlinePrimary,
      outlineLight,
      transparent,
      className,
      children,
      icon: Icon,
      isLoading,
      ...props
    },
    ref
  ) => {
    const colorClass = React.useMemo(() => {
      if (primary) return COLOR.primary;
      if (secondary) return COLOR.secondary;
      if (success) return COLOR.success;
      if (danger) return COLOR.danger;
      if (outlinePrimary) return COLOR.outlinePrimary;
      if (outlineLight) return COLOR.outlineLight;
      if (link) return COLOR.link;
      if (transparent) return COLOR.transparent;
      return COLOR.default;
    }, [
      primary,
      secondary,
      success,
      danger,
      outlinePrimary,
      outlineLight,
      link,
      transparent,
    ]);
    return (
      <button
        ref={ref}
        className={cn(
          'relative items-center border font-medium space-x-2 flex justify-center rounded-xl',
          isLoading ? 'cursor-not-allowed' : null,
          className,
          SIZE[size],
          colorClass
        )}
        type="button"
        disabled={isLoading}
        {...props}
      >
        {Icon && (
          <Icon className={cn('', ICON_SIZE[size])} aria-hidden="true" />
        )}
        {children && <span>{children}</span>}
      </button>
    );
  }
);

Button.defaultProps = {
  className: '',
  size: 'default',
  primary: false,
  secondary: false,
  success: false,
  danger: false,
  link: false,
  outlinePrimary: false,
  outlineLight: false,
  transparent: false,
  icon: null,
  children: null,
  isLoading: false,
};

Button.propTypes = {
  className: PropTypes.string,
  size: PropTypes.oneOf(['xxs', 'xs', 'sm', 'default', 'lg', 'xl']),
  primary: PropTypes.bool,
  secondary: PropTypes.bool,
  success: PropTypes.bool,
  danger: PropTypes.bool,
  link: PropTypes.bool,
  outlinePrimary: PropTypes.bool,
  outlineLight: PropTypes.bool,
  transparent: PropTypes.bool,
  icon: PropTypes.instanceOf(Object),
  children: PropTypes.oneOfType([
    PropTypes.instanceOf(Object),
    PropTypes.string,
    PropTypes.number,
  ]),
  isLoading: PropTypes.bool,
};

export default Button;
